import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { api, ConflictError, SessionExpired, type CriterionInput, type DependencyFacts, type EpicDetail, type Ticket } from '../api';
import { Markdown } from '../components/Markdown';
import { EpicCriteria } from './EpicCriteria';
import { OrderOfWork } from './OrderOfWork';
import { progressDenominator, STATUS_ORDER } from './status';

/**
 * One epic: what it is for, how we will know it is done, what belongs to it, and what
 * order that work can actually be done in.
 *
 * The dependency facts come from the server rather than being recomputed here, so the
 * answer an agent gets over MCP and the answer on this page cannot drift apart.
 */

function TicketCard({ ticket, depth, onOpen }: { ticket: Ticket; depth?: number; onOpen: () => void }) {
  return (
    <Card variant="outlined">
      <CardActionArea onClick={onOpen} sx={{ p: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="caption" color="primary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            DWLF-{ticket.ref}
          </Typography>
          {depth !== undefined && depth > 0 && (
            <Typography variant="caption" color="text.secondary">
              · {depth} deep
            </Typography>
          )}
        </Stack>
        <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
          {ticket.name}
        </Typography>
      </CardActionArea>
    </Card>
  );
}

/**
 * The facts, above the picture.
 *
 * Most tickets are linked to nothing, so a diagram alone is mostly empty space — while
 * "this is in progress behind something nobody has started" is a decision, and it is
 * invisible everywhere else on the board.
 */
function DependencyPanel({
  facts,
  tickets,
  board,
  onOpen,
}: {
  facts: DependencyFacts;
  tickets: Ticket[];
  board: Ticket[];
  onOpen: (ref: number) => void;
}) {
  const name = (ref: number) => board.find((ticket) => ticket.ref === ref)?.name ?? '';
  return (
    <Stack spacing={2}>
      {facts.blockedInProgress.length > 0 && (
        <Alert severity="warning">
          <Typography variant="subtitle2">Being built on sand</Typography>
          {facts.blockedInProgress.map((entry) => (
            <Typography key={entry.ref} variant="body2">
              <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => onOpen(entry.ref)}>
                DWLF-{entry.ref}
              </Box>{' '}
              is in progress behind{' '}
              {entry.blockedBy
                .map((blocker) => `DWLF-${blocker.ref} (${blocker.status ?? 'unknown'})`)
                .join(', ')}
            </Typography>
          ))}
        </Alert>
      )}

      {facts.clearedByClosure.length > 0 && (
        <Alert severity="info">
          <Typography variant="subtitle2">Cleared by a decision, not by delivery</Typography>
          {facts.clearedByClosure.map((entry) => (
            <Typography key={entry.ref} variant="body2">
              <Box
                component="span"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => onOpen(entry.ref)}
              >
                DWLF-{entry.ref}
              </Box>{' '}
              is only clear because {entry.closed.map((b) => `DWLF-${b.ref}`).join(', ')}{' '}
              {entry.closed.length === 1 ? 'was' : 'were'} closed — worth a re-read, since what
              it was designed against is not being built
            </Typography>
          ))}
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Ready to start · {facts.readyToStart.length}
        </Typography>
        {facts.readyToStart.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing in this epic is both unstarted and unblocked.
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {facts.readyToStart.map((ref) => (
              <Chip
                key={ref}
                size="small"
                label={`DWLF-${ref}`}
                title={name(ref)}
                onClick={() => onOpen(ref)}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Order of work
        </Typography>
        <OrderOfWork facts={facts} tickets={board} inEpic={tickets} onOpen={onOpen} />
      </Box>
    </Stack>
  );
}

function Goal({
  goal,
  saving,
  onSave,
}: {
  goal: string;
  saving: boolean;
  onSave: (next: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  if (draft === null) {
    return (
      <Stack spacing={1}>
        {goal ? (
          <Markdown>{goal}</Markdown>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No goal stated yet.
          </Typography>
        )}
        <Box>
          <Button size="small" onClick={() => setDraft(goal)}>
            {goal ? 'Edit goal' : 'Add goal'}
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <TextField
        multiline
        minRows={4}
        fullWidth
        value={draft}
        placeholder="What is this epic for? Markdown."
        onChange={(event) => setDraft(event.target.value)}
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={() => setDraft(null)} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={saving}
          onClick={async () => {
            await onSave(draft);
            setDraft(null);
          }}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  );
}

function EpicHeader({
  name,
  archived,
  counts,
  onBack,
}: {
  name: string;
  archived: boolean;
  counts: { ticketCount: number; doneCount: number; closedCount: number };
  onBack: () => void;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
      <Button size="small" onClick={onBack}>
        ← Board
      </Button>
      <Typography variant="h5" fontWeight={600}>
        {name}
      </Typography>
      {archived && <Chip size="small" label="Archived" />}
      <Box sx={{ flex: 1 }} />
      <Chip size="small" variant="outlined" label={`${counts.ticketCount} tickets`} />
      <Chip
        size="small"
        variant="outlined"
        color="success"
        label={`${counts.doneCount} of ${progressDenominator(counts)} done`}
      />
      {/* Beside `done`, never added to it. */}
      {counts.closedCount > 0 && (
        <Chip size="small" variant="outlined" label={`${counts.closedCount} closed`} />
      )}
    </Stack>
  );
}

/** The epic's tickets, in the same columns the board uses. */
function TicketColumns({
  tickets,
  statuses,
  chainDepth,
  onOpen,
}: {
  tickets: Ticket[];
  /** The board's statuses, from `/v1/meta` — the server owns the vocabulary. */
  statuses: string[];
  chainDepth: Record<number, number>;
  onOpen: (ref: number) => void;
}) {
  return (
    <>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1 }}>
        Tickets
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: `repeat(${statuses.length}, minmax(240px, 1fr))` },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {statuses.map((status) => {
          const column = tickets.filter((ticket) => ticket.status === status);
          return (
            <Stack key={status} spacing={1}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={600}>
                  {status}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {column.length}
                </Typography>
              </Stack>
              {column.map((ticket) => (
                <TicketCard
                  key={ticket.ref}
                  ticket={ticket}
                  depth={chainDepth[ticket.ref]}
                  onOpen={() => onOpen(ticket.ref)}
                />
              ))}
            </Stack>
          );
        })}
      </Box>
    </>
  );
}

/**
 * Loading and saving one epic.
 *
 * A 409 is surfaced, never resolved automatically: criteria are a whole list, and quietly
 * merging two people's edits is exactly how a tick disappears with nobody the wiser.
 * Re-reading is cheap, so the honest answer is to say so and offer Reload.
 */
function useEpic(name: string) {
  const [detail, setDetail] = useState<EpicDetail | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      setDetail(await api.getEpic(name));
    } catch (cause) {
      // A session expiry is handled globally by AuthProvider; showing it here too would
      // put an error behind the sign-in prompt.
      if (cause instanceof SessionExpired) return;
      setProblem(cause instanceof Error ? cause.message : 'Could not load this epic.');
    }
  }, [name]);

  useEffect(() => {
    setDetail(null);
    void load();
  }, [load]);

  const save = async (fields: { goal?: string; criteria?: CriterionInput[] }) => {
    if (!detail) return;
    setSaving(true);
    setProblem(null);
    try {
      const { epic } = await api.updateEpic(name, detail.epic.version, fields);
      setDetail({ ...detail, epic });
    } catch (cause) {
      if (cause instanceof SessionExpired) return;
      setProblem(
        cause instanceof ConflictError
          ? `${cause.message} Reload to pick up their version — your unsaved wording is not sent.`
          : cause instanceof Error
            ? cause.message
            : 'Could not save.',
      );
    } finally {
      setSaving(false);
    }
  };

  return { detail, problem, saving, load, save };
}

/** Before the epic is on screen there are only two states: still coming, or it failed. */
function NotLoaded({ problem, onBack }: { problem: string | null; onBack: () => void }) {
  if (problem) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={onBack}>Back to board</Button>}>
          {problem}
        </Alert>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );
}

export function EpicPage({
  name,
  board,
  statuses,
  onOpenTicket,
  onBack,
}: {
  name: string;
  /** Every ticket the board holds — a blocker often lives in another epic. */
  board: Ticket[];
  /**
   * The statuses to lay out as columns — the server's vocabulary, resolved by `App` so
   * this page and the Board tab cannot disagree.
   */
  statuses: string[];
  onOpenTicket: (ref: number) => void;
  onBack: () => void;
}) {
  const { detail, problem, saving, load, save } = useEpic(name);

  if (!detail) return <NotLoaded problem={problem} onBack={onBack} />;

  const { epic, tickets, dependencies } = detail;
  const byStatus = (status: string) => tickets.filter((ticket) => ticket.status === status);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <EpicHeader
        name={epic.name}
        archived={epic.archived}
        counts={{
          ticketCount: tickets.length,
          doneCount: byStatus('Done').length,
          closedCount: byStatus('Closed').length,
        }}
        onBack={onBack}
      />

      {problem && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button onClick={() => void load()}>Reload</Button>}>
          {problem}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Goal
          </Typography>
          <Goal goal={epic.goal} saving={saving} onSave={(goal) => save({ goal })} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">
            Success criteria
          </Typography>
          <Box sx={{ mt: 1 }}>
            <EpicCriteria
              criteria={epic.criteria ?? []}
              saving={saving}
              onSave={(criteria) => save({ criteria })}
            />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Dependencies
          </Typography>
          <Box sx={{ mt: 1 }}>
            <DependencyPanel facts={dependencies} tickets={tickets} board={board} onOpen={onOpenTicket} />
          </Box>
        </Paper>
      </Box>

      {/* The empty case is held here rather than left as a caller's precondition: an
          empty list emits `repeat(0, …)`, which is invalid and ignored, so every ticket
          in the epic would vanish while the header above went on counting them. */}
      <TicketColumns
        tickets={tickets}
        statuses={statuses.length ? statuses : STATUS_ORDER}
        chainDepth={dependencies.chainDepth}
        onOpen={onOpenTicket}
      />
    </Box>
  );
}
